import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';

const COLORS = [
  '#10b981', // green
  '#ef4444', // red
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#84cc16', // lime
];

/**
 * PieChart Component for Yes/No and simple multiple choice questions
 */
const PieChart = ({ data }) => {
  if (!data || data.type === 'empty') {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No data to display
      </div>
    );
  }

  // Handle Yes/No questions
  if (data.type === 'yes_no') {
    const chartData = [
      { name: 'Yes', value: data.yesCount, percentage: data.yes },
      { name: 'No', value: data.noCount, percentage: data.no }
    ].filter(item => item.value > 0);

    if (chartData.length === 0) {
      return (
        <div className="text-center py-4 text-muted-foreground">
          No valid responses
        </div>
      );
    }

    return (
      <div className="w-full">
        <ChartContainer config={{}} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.name === 'Yes' ? COLORS[0] : COLORS[1]} 
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [
                  `${value} responses (${chartData.find(d => d.name === name)?.percentage ?? 0}%)`,
                  name
                ]}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Summary */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
            <p className="text-sm text-muted-foreground">Yes</p>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
              {data.yesCount} ({data.yes}%)
            </p>
          </div>
          <div className="text-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
            <p className="text-sm text-muted-foreground">No</p>
            <p className="text-lg font-semibold text-red-600 dark:text-red-400">
              {data.noCount} ({data.no}%)
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Handle multiple choice with frequency distribution
  if (data.type === 'frequency' && data.distribution && 
      typeof data.distribution === 'object' && 
      data.distribution !== null && 
      !Array.isArray(data.distribution)) {
    const entries = Object.entries(data.distribution);

    const total = entries.reduce((sum, [, count]) => sum + count, 0);
    
    // Define experience range order for proper sorting
    const experienceOrder = [
      'Less than 1 year',
      '1-2 years', 
      '3-5 years',
      '6-10 years',
      '11-15 years',
      '15+ years'
    ];
    
    // Check if this is experience data based on the range labels
    const isExperienceData = data.isExperienceData || 
      entries.some(([option]) => experienceOrder.includes(option));
    
    const chartData = entries
      .sort((a, b) => {
        if (isExperienceData) {
          // Sort by experience order
          const aIndex = experienceOrder.indexOf(a[0]);
          const bIndex = experienceOrder.indexOf(b[0]);
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
        }
        // Default sort by count descending
        return b[1] - a[1];
      })
      .map(([option, count]) => ({
        name: option.length > 20 ? option.substring(0, 20) + '...' : option,
        fullName: option,
        value: count,
        percentage: Math.round((count / total) * 100)
      }));
    
    // Debug log for experience data
    if (isExperienceData && import.meta.env.DEV) {
      console.log('Experience pie chart data order:', chartData.map(d => d.name));
    }

    return (
      <div className="w-full">
        <ChartContainer config={{}} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `${value} responses (${props.payload.percentage}%)`,
                  props.payload.fullName
                ]}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Most popular response */}
        {data.mode && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Most Popular Response</p>
            <p className="font-medium">{data.mode}</p>
            <p className="text-sm text-muted-foreground">
              {data.distribution[data.mode]} responses ({Math.round((data.distribution[data.mode] / total) * 100)}%)
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-center py-4 text-muted-foreground">
      Unable to create pie chart for this data type
    </div>
  );
};

export default PieChart;